// scenes/produits/StockDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  TransferWithinAStation as TransferIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { produitService } from 'services/produitService';

const StockDialog = ({ open, onClose, produit, onSuccess }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadingMouvements, setLoadingMouvements] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [mouvements, setMouvements] = useState([]);

  // États du formulaire d'ajustement
  const [ajustementData, setAjustementData] = useState({
    type_mouvement: 'entree', // entree, sortie, correction
    quantite: '',
    motif: '',
    reference: '',
  });

  // Charger l'historique des mouvements
  const loadMouvements = async () => {
    if (!produit) return;
    
    try {
      setLoadingMouvements(true);
      const response = await produitService.getMouvements(produit.id);
      setMouvements(response);
    } catch (err) {
      console.error('Erreur de chargement des mouvements:', err);
    } finally {
      setLoadingMouvements(false);
    }
  };

  useEffect(() => {
    if (open && produit) {
      setAjustementData({
        type_mouvement: 'entree',
        quantite: '',
        motif: '',
        reference: '',
      });
      setError('');
      setSuccess('');
      loadMouvements();
    }
  }, [open, produit]);

  // Gestion des changements
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAjustementData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validation du formulaire
  const validateForm = () => {
    if (!ajustementData.quantite || parseFloat(ajustementData.quantite) <= 0) {
      setError('La quantité doit être supérieure à 0');
      return false;
    }

    if (ajustementData.type_mouvement === 'sortie') {
      const nouvelleQuantite = produit.stock_actuel - parseFloat(ajustementData.quantite);
      if (nouvelleQuantite < 0) {
        setError('Stock insuffisant pour cette sortie');
        return false;
      }
    }

    if (!ajustementData.motif.trim()) {
      setError('Le motif est obligatoire');
      return false;
    }

    return true;
  };

  // Soumission de l'ajustement
  const handleSubmitAjustement = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await produitService.ajusterStock(
        produit.id,
        parseFloat(ajustementData.quantite),
        ajustementData.type_mouvement,
        ajustementData.motif
      );

      setSuccess('Mouvement de stock enregistré avec succès');
      setAjustementData({
        type_mouvement: 'entree',
        quantite: '',
        motif: '',
        reference: '',
      });

      // Recharger les données
      onSuccess();
      loadMouvements();
      
      // Fermer automatiquement après 2 secondes
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Erreur de soumission:', err);
      setError(err.response?.data?.detail || err.message || 'Erreur lors de l\'ajustement du stock');
    } finally {
      setLoading(false);
    }
  };

  // Formatage de la date
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Couleur du type de mouvement
  const getMouvementColor = (type) => {
    switch (type) {
      case 'entree': return 'success';
      case 'sortie': return 'error';
      case 'correction': return 'warning';
      case 'inventaire': return 'info';
      default: return 'default';
    }
  };

  // Icône du type de mouvement
  const getMouvementIcon = (type) => {
    switch (type) {
      case 'entree': return <AddIcon />;
      case 'sortie': return <RemoveIcon />;
      case 'correction': return <TransferIcon />;
      case 'inventaire': return <InventoryIcon />;
      default: return null;
    }
  };

  // Texte du type de mouvement
  const getMouvementText = (type) => {
    switch (type) {
      case 'entree': return 'Entrée';
      case 'sortie': return 'Sortie';
      case 'correction': return 'Correction';
      case 'inventaire': return 'Inventaire';
      default: return type;
    }
  };

  if (!produit) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <InventoryIcon color="primary" />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Gestion du stock
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {produit.nom} - Code: {produit.code || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Informations rapides du produit */}
        <Paper sx={{ p: 3, m: 3, backgroundColor: theme.palette.grey[50] }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {produit.stock_actuel}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Stock actuel
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {produit.stock_minimum}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Stock minimum
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {produit.stock_maximum || '∞'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Stock maximum
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Ajuster le stock" icon={<AddIcon />} iconPosition="start" />
            <Tab label="Historique" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Contenu des tabs */}
        <Box sx={{ p: 3 }}>
          {activeTab === 0 ? (
            // Formulaire d'ajustement
            <form onSubmit={handleSubmitAjustement}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type de mouvement</InputLabel>
                    <Select
                      value={ajustementData.type_mouvement}
                      onChange={handleChange}
                      name="type_mouvement"
                      label="Type de mouvement"
                      disabled={loading}
                    >
                      <MenuItem value="entree">
                        <Box display="flex" alignItems="center" gap={1}>
                          <AddIcon color="success" />
                          <Typography>Entrée de stock</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="sortie">
                        <Box display="flex" alignItems="center" gap={1}>
                          <RemoveIcon color="error" />
                          <Typography>Sortie de stock</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="correction">
                        <Box display="flex" alignItems="center" gap={1}>
                          <TransferIcon color="warning" />
                          <Typography>Correction</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="inventaire">
                        <Box display="flex" alignItems="center" gap={1}>
                          <InventoryIcon color="info" />
                          <Typography>Inventaire</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Quantité"
                    name="quantite"
                    type="number"
                    value={ajustementData.quantite}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    disabled={loading}
                    inputProps={{ step: "0.01", min: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {ajustementData.type_mouvement === 'entree' ? (
                            <TrendingUpIcon color="success" />
                          ) : (
                            <TrendingDownIcon color="error" />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Motif *"
                    name="motif"
                    value={ajustementData.motif}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    disabled={loading}
                    multiline
                    rows={2}
                    helperText="Ex: Réception de commande, Vente au détail, Inventaire, etc."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Référence (optionnel)"
                    name="reference"
                    value={ajustementData.reference}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading}
                    helperText="Numéro de commande, nom du client, etc."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Nouveau stock prévisionnel:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h5">
                        {produit.stock_actuel}
                      </Typography>
                      <Typography variant="h5" color={ajustementData.type_mouvement === 'entree' ? 'success.main' : 'error.main'}>
                        {ajustementData.type_mouvement === 'entree' ? '+' : '-'}
                        {ajustementData.quantite || 0}
                      </Typography>
                      <Typography variant="h5">
                        =
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={
                        (ajustementData.type_mouvement === 'entree' 
                          ? produit.stock_actuel + (parseFloat(ajustementData.quantite) || 0)
                          : produit.stock_actuel - (parseFloat(ajustementData.quantite) || 0)
                        ) <= produit.stock_minimum ? 'warning.main' : 'success.main'
                      }>
                        {ajustementData.type_mouvement === 'entree' 
                          ? produit.stock_actuel + (parseFloat(ajustementData.quantite) || 0)
                          : produit.stock_actuel - (parseFloat(ajustementData.quantite) || 0)
                        }
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <DialogActions sx={{ p: 0, mt: 3 }}>
                <Button onClick={onClose} disabled={loading}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                  color={ajustementData.type_mouvement === 'entree' ? 'success' : 'error'}
                >
                  {loading ? 'Enregistrement...' : `Enregistrer ${getMouvementText(ajustementData.type_mouvement)}`}
                </Button>
              </DialogActions>
            </form>
          ) : (
            // Historique des mouvements
            <Box>
              {loadingMouvements ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : mouvements.length === 0 ? (
                <Alert severity="info">
                  Aucun mouvement de stock enregistré pour ce produit.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Quantité</TableCell>
                        <TableCell>Motif</TableCell>
                        <TableCell>Référence</TableCell>
                        <TableCell>Utilisateur</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mouvements.map((mouvement, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(mouvement.date_creation)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getMouvementIcon(mouvement.type_mouvement)}
                              label={getMouvementText(mouvement.type_mouvement)}
                              color={getMouvementColor(mouvement.type_mouvement)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={mouvement.type_mouvement === 'entree' ? 'success.main' : 'error.main'}
                            >
                              {mouvement.type_mouvement === 'entree' ? '+' : '-'}
                              {mouvement.quantite}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {mouvement.motif}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {mouvement.reference ? (
                              <Chip label={mouvement.reference} size="small" variant="outlined" />
                            ) : (
                              <Typography variant="body2" color="textSecondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {mouvement.utilisateur?.username || 'System'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StockDialog;